/**
 * Delivery Reconciliation Context
 * 
 * Provides access to reconciliation results across the application.
 * Runs reconciliation periodically and exposes results for:
 * - Admin UI (future)
 * - Pool selection integration
 * - Retry worker integration
 * 
 * This context wraps the pure reconciliation engine and manages:
 * - Periodic execution
 * - Result caching
 * - Manual trigger capability
 */

import { createContext, useContext, useState, useCallback, useEffect, ReactNode, useMemo, useRef } from 'react';
import { useContracts } from './ContractsContext';
import {
  runDeliveryReconciliation,
  type ReconciliationResult,
  type ReconciliationConfig,
  type DeliveryStatus,
  type ContractEvaluation,
  DEFAULT_RECONCILIATION_CONFIG,
} from '@/lib/deliveryReconciliation';

// Re-export types for consumers
export type { ReconciliationResult, ReconciliationConfig, DeliveryStatus, ContractEvaluation };

// --- Context State ---

interface ReconciliationState {
  /** Latest reconciliation result */
  lastResult: ReconciliationResult | null;
  /** Whether a reconciliation is currently running */
  isRunning: boolean;
  /** Timestamp of last successful run */
  lastRunAt: number | null;
  /** Number of runs since context mounted */
  runCount: number;
  /** Current config being used */
  config: ReconciliationConfig;
}

interface ReconciliationActions {
  /** Manually trigger a reconciliation run */
  runReconciliation: () => ReconciliationResult;
  /** Update configuration */
  updateConfig: (config: Partial<ReconciliationConfig>) => void;
  /** Get agents that should have increased retry priority */
  getRetryPriorityAgents: () => string[];
  /** Get agents that should have expanded pool inclusion */
  getPoolExpansionAgents: () => Map<string, number>;
  /** Get agents that should have boosted guaranteed minimum */
  getGuaranteedBoostAgents: () => Map<string, number>;
  /** Get evaluation for a specific contract */
  getContractEvaluation: (contractId: string) => ContractEvaluation | null;
  /** Get all evaluations for an agent */
  getAgentEvaluations: (agentId: string) => ContractEvaluation[];
  /** Check if an agent has at-risk contracts */
  isAgentAtRisk: (agentId: string) => boolean;
}

// --- Default Interval ---

const DEFAULT_RECONCILIATION_INTERVAL_MS = 60 * 1000; // 1 minute for demo
const _PRODUCTION_RECONCILIATION_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes for production

// --- Context ---

const ReconciliationContext = createContext<{
  state: ReconciliationState;
  actions: ReconciliationActions;
} | undefined>(undefined);

// --- Provider ---

interface ReconciliationProviderProps {
  children: ReactNode;
  /** Interval between automatic reconciliation runs (ms) */
  intervalMs?: number;
  /** Whether to run reconciliation automatically */
  autoRun?: boolean;
}

export function ReconciliationProvider({
  children,
  intervalMs = DEFAULT_RECONCILIATION_INTERVAL_MS,
  autoRun = true,
}: ReconciliationProviderProps) {
  const { state: contractsState } = useContracts();
  
  const [lastResult, setLastResult] = useState<ReconciliationResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [lastRunAt, setLastRunAt] = useState<number | null>(null);
  const [runCount, setRunCount] = useState(0);
  const [config, setConfig] = useState<ReconciliationConfig>(DEFAULT_RECONCILIATION_CONFIG);
  
  // Ref to prevent concurrent runs
  const runningRef = useRef(false);
  
  // Run reconciliation
  const runReconciliation = useCallback((): ReconciliationResult => {
    // Prevent concurrent runs
    if (runningRef.current) {
      if (lastResult) return lastResult;
      // Return empty result if no previous result
      return {
        evaluatedAt: Date.now(),
        configUsed: config,
        summary: {
          totalContracts: 0,
          activeContracts: 0,
          onTrack: 0,
          behind: 0,
          atRisk: 0,
          overdelivering: 0,
          totalCommitted: 0,
          totalDelivered: 0,
          overallDeliveryRate: 0,
          infeasibleCount: 0,
        },
        contractEvaluations: [],
        systemRecommendations: {
          increaseRetryPriority: [],
          expandPoolInclusion: [],
          boostGuaranteedMinimum: [],
          reducePacing: [],
          warnings: [],
        },
      };
    }
    
    runningRef.current = true;
    setIsRunning(true);
    
    try {
      const result = runDeliveryReconciliation(
        contractsState.contracts,
        Date.now(),
        config
      );
      
      setLastResult(result);
      setLastRunAt(result.evaluatedAt);
      setRunCount(prev => prev + 1);
      
      return result;
    } finally {
      runningRef.current = false;
      setIsRunning(false);
    }
  }, [contractsState.contracts, config, lastResult]);
  
  // Update configuration
  const updateConfig = useCallback((updates: Partial<ReconciliationConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  }, []);
  
  // Get agents that should have increased retry priority
  const getRetryPriorityAgents = useCallback((): string[] => {
    if (!lastResult) return [];
    return lastResult.systemRecommendations.increaseRetryPriority.map(r => r.agentId);
  }, [lastResult]);
  
  // Get agents with pool expansion recommendations
  const getPoolExpansionAgents = useCallback((): Map<string, number> => {
    const map = new Map<string, number>();
    if (!lastResult) return map;
    
    for (const rec of lastResult.systemRecommendations.expandPoolInclusion) {
      map.set(rec.agentId, rec.recommendedBoost);
    }
    
    return map;
  }, [lastResult]);
  
  // Get agents with guaranteed boost recommendations
  const getGuaranteedBoostAgents = useCallback((): Map<string, number> => {
    const map = new Map<string, number>();
    if (!lastResult) return map;
    
    for (const rec of lastResult.systemRecommendations.boostGuaranteedMinimum) {
      map.set(rec.agentId, rec.recommendedBoostIncrease);
    }
    
    return map;
  }, [lastResult]);
  
  // Get evaluation for a specific contract
  const getContractEvaluation = useCallback((contractId: string): ContractEvaluation | null => {
    if (!lastResult) return null;
    return lastResult.contractEvaluations.find(e => e.contractId === contractId) || null;
  }, [lastResult]);
  
  // Get all evaluations for an agent
  const getAgentEvaluations = useCallback((agentId: string): ContractEvaluation[] => {
    if (!lastResult) return [];
    return lastResult.contractEvaluations.filter(e => e.agentId === agentId);
  }, [lastResult]);
  
  // Check if an agent has at-risk contracts
  const isAgentAtRisk = useCallback((agentId: string): boolean => {
    if (!lastResult) return false;
    return lastResult.contractEvaluations.some(
      e => e.agentId === agentId && e.deliveryStatus === 'AT_RISK'
    );
  }, [lastResult]);
  
  // Auto-run reconciliation on interval
  useEffect(() => {
    if (!autoRun) return;
    
    // Run immediately on mount
    runReconciliation();
    
    // Set up interval
    const interval = setInterval(() => {
      runReconciliation();
    }, intervalMs);
    
    return () => clearInterval(interval);
  }, [autoRun, intervalMs]); // Intentionally not including runReconciliation to avoid infinite loops
  
  // Re-run when contracts change significantly
  useEffect(() => {
    // Only re-run if we have a previous result and contracts changed
    if (lastResult && contractsState.contracts.length > 0) {
      // Debounce: only re-run if contracts actually changed
      const timer = setTimeout(() => {
        runReconciliation();
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [contractsState.contracts]); // Intentionally limited dependencies
  
  const state: ReconciliationState = useMemo(() => ({
    lastResult,
    isRunning,
    lastRunAt,
    runCount,
    config,
  }), [lastResult, isRunning, lastRunAt, runCount, config]);
  
  const actions: ReconciliationActions = useMemo(() => ({
    runReconciliation,
    updateConfig,
    getRetryPriorityAgents,
    getPoolExpansionAgents,
    getGuaranteedBoostAgents,
    getContractEvaluation,
    getAgentEvaluations,
    isAgentAtRisk,
  }), [
    runReconciliation,
    updateConfig,
    getRetryPriorityAgents,
    getPoolExpansionAgents,
    getGuaranteedBoostAgents,
    getContractEvaluation,
    getAgentEvaluations,
    isAgentAtRisk,
  ]);
  
  return (
    <ReconciliationContext.Provider value={{ state, actions }}>
      {children}
    </ReconciliationContext.Provider>
  );
}

// --- Hook ---

export function useReconciliation() {
  const context = useContext(ReconciliationContext);
  if (!context) {
    throw new Error('useReconciliation must be used within a ReconciliationProvider');
  }
  return context;
}

export default ReconciliationContext;
